// =========================================================================
// AlertRoutingPanel.transform — pure projections behind the alert page's
// root-cause and routing panel. Issues module, fork-owned (zone C).
//
// The panel puts the two signals the reliability engine weighs side by side:
//
//   - operator — who carries the component's operator-sensitive events,
//     against their share of the machine's hours (a stacked bar, two rows);
//   - equipment — the component's condition score and the drift on its own
//     tracked metric;
//
// then the day / night split, the evidence, and the route cards.
//
// Transformation layer: pure functions over engine outputs and the
// attribution. No React, no engine mutations.
//
// @see docs/logic/issues/alert-routing.md
// =========================================================================

import { HEALTH } from '@/versions/v1/data/core';
import { bandOf } from '@/versions/v1/schema/machine';
import type { RootCauseFinding, ShiftCode } from '@/versions/v1/schema/operations';
import type { DailyMetricRow } from '@/versions/v1/schema/telemetry';
import type { Attribution, MachineOperatorShare } from '@/versions/v1/shared/reliability';
import { CAUSE_THRESHOLD, METRIC_COMPONENT, driftOf } from '@/versions/v1/shared/reliability';
import { metricInfoFor } from '@/versions/v1/shared/seed/metric-info';

// ---------------------------------------------------------------------------
// Operator signal — events share vs hours share
// ---------------------------------------------------------------------------

/** Operators beyond this many fold into "Others" on the chart. */
export const MAX_OPERATOR_SEGMENTS = 4;

/** Categorical fills for the operator segments; the top operator always leads. */
const OPERATOR_FILLS = ['var(--info)', 'var(--band-good)', 'var(--warn)', 'var(--band-major)'];
const OTHERS_FILL = 'var(--ink-tertiary)';

export interface OperatorChart {
  /** Segment order — top operator first, "Others" last when present. */
  readonly categories: readonly string[];
  readonly colors: Readonly<Record<string, string>>;
  /** Two rows: share of hours, share of events, each summing to 100. */
  readonly rows: readonly { label: string; values: Readonly<Record<string, number>> }[];
  /** Per-operator lines for the legend table, same order as `categories`. */
  readonly lines: readonly {
    readonly name: string;
    readonly crew: string;
    readonly hours: number;
    readonly hoursPct: number;
    readonly events: number;
    readonly eventsPct: number;
    readonly synthesised: boolean;
    readonly color: string;
    readonly top: boolean;
  }[];
  readonly totalEvents: number;
}

/**
 * Share of hours vs share of events per operator, as percentages for a
 * two-row {@link StackedBar}.
 *
 * Sort: the top operator (the one the finding names) first, then by events,
 * then by hours. Operators past {@link MAX_OPERATOR_SEGMENTS} fold into
 * "Others". Invariant: each row sums to 100 (± rounding) when there are
 * hours / events at all.
 */
export function operatorChart(
  shares: readonly MachineOperatorShare[],
  topOperatorId: string | null,
): OperatorChart {
  const sorted = shares
    .slice()
    .sort(
      (a, b) =>
        Number(b.operatorId === topOperatorId) - Number(a.operatorId === topOperatorId) ||
        b.events - a.events ||
        b.hours - a.hours,
    );
  const head = sorted.slice(0, MAX_OPERATOR_SEGMENTS);
  const tail = sorted.slice(MAX_OPERATOR_SEGMENTS);
  const totalEvents = shares.reduce((s, o) => s + o.events, 0);

  const lines: OperatorChart['lines'][number][] = head.map((o, i) => ({
    name: o.name,
    crew: o.crew as string,
    hours: o.hours,
    hoursPct: o.hoursShare * 100,
    events: o.events,
    eventsPct: o.eventShare * 100,
    synthesised: o.synthesised,
    color: OPERATOR_FILLS[i] ?? OTHERS_FILL,
    top: o.operatorId === topOperatorId,
  }));
  if (tail.length) {
    lines.push({
      name: `Others (${tail.length})`,
      crew: '',
      hours: Math.round(tail.reduce((s, o) => s + o.hours, 0) * 10) / 10,
      hoursPct: tail.reduce((s, o) => s + o.hoursShare, 0) * 100,
      events: tail.reduce((s, o) => s + o.events, 0),
      eventsPct: tail.reduce((s, o) => s + o.eventShare, 0) * 100,
      synthesised: tail.some((o) => o.synthesised),
      color: OTHERS_FILL,
      top: false,
    });
  }

  const categories = lines.map((l) => l.name);
  const colors = Object.fromEntries(lines.map((l) => [l.name, l.color]));
  return {
    categories,
    colors,
    rows: [
      { label: 'Share of hours', values: Object.fromEntries(lines.map((l) => [l.name, l.hoursPct])) },
      { label: 'Share of events', values: Object.fromEntries(lines.map((l) => [l.name, l.eventsPct])) },
    ],
    lines,
    totalEvents,
  };
}

/** Chance label for the concentration: "p < 0.001", "p = 0.014", or null. */
export function chanceLabel(pValue: number | null): string | null {
  if (pValue === null) return null;
  if (pValue < 0.001) return 'p < 0.001';
  return `p = ${pValue < 0.01 ? pValue.toFixed(3) : pValue.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Equipment signal — condition and drift
// ---------------------------------------------------------------------------

export interface EquipmentView {
  readonly score: number | null;
  readonly bandLabel: string | null;
  readonly bandColor: string | null;
  readonly note: string | null;
  /** Drift on the component's own tracked metric, when it has one. */
  readonly drift: {
    readonly metricName: string;
    readonly unit: string;
    readonly first: number;
    readonly last: number;
    readonly pctPerDay: number;
    /** Beyond the engine's 0.4 %/day drift rule. */
    readonly drifting: boolean;
  } | null;
}

/** Condition register row + daily-metric drift for one machine × component. */
export function equipmentView(
  machine: string,
  component: string,
  dailyMetric: readonly DailyMetricRow[],
): EquipmentView {
  const reg = HEALTH.find((h) => h.machine_id === machine && h.component === component) ?? null;
  const band = reg ? bandOf(reg.health_score) : null;
  const d = driftOf(machine, dailyMetric);
  const onComponent = d.metric !== null && new RegExp(METRIC_COMPONENT[d.metric], 'i').test(component);
  const info = d.metric ? metricInfoFor(d.metric) : null;
  return {
    score: reg?.health_score ?? null,
    bandLabel: band?.label ?? null,
    bandColor: band?.colorToken ?? null,
    note: reg?.note ?? null,
    drift:
      onComponent && info && d.first !== null && d.last !== null
        ? {
            metricName: info.name,
            unit: info.unit,
            first: d.first,
            last: d.last,
            pctPerDay: d.pctPerDay,
            drifting: Math.abs(d.pctPerDay) > 0.4,
          }
        : null,
  };
}

// ---------------------------------------------------------------------------
// Signal meters
// ---------------------------------------------------------------------------

export interface SignalMeter {
  /** 0–100 fill. */
  readonly pct: number;
  /** Where the calling threshold sits, 0–100. */
  readonly thresholdPct: number;
  readonly calls: boolean;
  readonly label: string;
}

/** A 0–1 signal as a meter with the engine's calling threshold marked. */
export function signalMeter(value: number): SignalMeter {
  const calls = value >= CAUSE_THRESHOLD;
  return {
    pct: Math.round(Math.max(0, Math.min(1, value)) * 100),
    thresholdPct: CAUSE_THRESHOLD * 100,
    calls,
    label: value <= 0 ? 'no signal' : calls ? `strong · ${value.toFixed(2)}` : `weak · ${value.toFixed(2)}`,
  };
}

// ---------------------------------------------------------------------------
// Day vs night
// ---------------------------------------------------------------------------

export interface ShiftSplitRow {
  readonly shift: ShiftCode;
  readonly label: string;
  readonly hours: number;
  readonly hoursPct: number;
  readonly events: number;
  readonly eventsPct: number;
  /** Events per 100 operating hours on this shift. */
  readonly ratePer100h: number;
}

/**
 * The machine's hours and the component's operator-sensitive events, day
 * shift against night shift. Same event filter as the engine's operator
 * signal (sensitivity ≠ none, component matched), so the totals agree with
 * the chart above it.
 */
export function shiftSplit(att: Attribution, machine: string, component: string): ShiftSplitRow[] {
  const hours: Record<ShiftCode, number> = { D: 0, N: 0 };
  const events: Record<ShiftCode, number> = { D: 0, N: 0 };
  for (const a of att.assignments) if (a.machine === machine) hours[a.shift] += a.hours;
  for (const e of att.events) {
    if (e.machine !== machine || e.component !== component || e.sensitivity === 'none') continue;
    events[e.shift] += e.count;
  }
  const H = hours.D + hours.N;
  const E = events.D + events.N;
  return (['D', 'N'] as const).map((s) => ({
    shift: s,
    label: s === 'D' ? 'Day shift' : 'Night shift',
    hours: Math.round(hours[s] * 10) / 10,
    hoursPct: H ? (hours[s] / H) * 100 : 0,
    events: events[s],
    eventsPct: E ? (events[s] / E) * 100 : 0,
    ratePer100h: hours[s] ? Math.round((events[s] / hours[s]) * 1000) / 10 : 0,
  }));
}

// ---------------------------------------------------------------------------
// Headline
// ---------------------------------------------------------------------------

/** One sentence that says what the verdict rests on. */
export function verdictLine(finding: RootCauseFinding): string {
  const top = finding.topOperator;
  const opPart =
    top && finding.operatorSignal >= CAUSE_THRESHOLD
      ? `${top.name} carries ${Math.round(top.eventShare * 100)}% of the events on ${Math.round(top.hoursShare * 100)}% of the hours`
      : null;
  switch (finding.cause) {
    case 'mixed':
      return `Both: the component is worn, and ${opPart ?? 'one operator carries most of the events'}. Maintenance fixes the wear; operations fixes the technique.`;
    case 'operator':
      return `${opPart ?? 'Events concentrate on one operator'}, and the component's condition does not explain it. This is a coaching conversation, not a part.`;
    case 'equipment':
      return `The component is going, and its events are spread across the operators in line with their hours. This is a part and a window, not a driver.`;
    case 'insufficient':
    default:
      return 'Not enough events and no condition signal to call it either way.';
  }
}
